import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import PlantillaScreen from "../../../helpers/PlantillaScreen";
import request from "../../../../Helpers/API";
import Swal from "sweetalert2";
import { Avatar } from "@mui/material";
import { LoaderScreen } from "../../../components/LoaderScreen";
import { ButtonBack } from "../Util/ButtonBack";
import {
  obtenerTextoEstado,
  obtenerClaseEstado,
  obtenerTextoPrioridad,
  obtenerClasePrioridad,
  obtenerTransicionesDisponibles,
  ESTATUS_TERMINALES,
} from "../../../../Helpers/SeguimientoActEstatus";

import VisibilityIcon from "@mui/icons-material/Visibility";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EventIcon from "@mui/icons-material/Event";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";

// Pantalla de detalle de un ticket ("Ver" en el listado). Muestra la
// información completa + el historial de eventos, y es el único lugar
// donde se puede cambiar el estatus del ticket (botón "Editar estatus"),
// acción reservada exclusivamente al responsable de la sala -- el botón ya
// se oculta si no lo es, pero además el backend (SeguimientoAct/CambiarEstatus)
// vuelve a validarlo contra la base, así que no depende de lo que muestre
// el frontend.
export const SeguimientoActividadesCardScreen = () => {

  const { id } = useParams();

  const [actividad, setActividad] = useState({});
  const [historial, setHistorial] = useState([]);
  const [comentario, setComentario] = useState("");
  const [loader, setLoader] = useState({ mostrar: false });
  const [loading, setLoading] = useState(false);

  // Campos extra configurados para la sala del ticket (Cat_Sala.camposExtra),
  // solo para mostrarlos aquí -- la definición dice qué campos existen y
  // datosExtra (ya viene en el propio ticket) trae los valores capturados.
  const [camposExtraDefinicion, setCamposExtraDefinicion] = useState([]);

  // Checklist de subactividades (opcional -- vacío si la actividad del
  // ticket no tiene ninguna configurada, y en ese caso todo lo demás se
  // comporta exactamente igual que antes). Solo el responsable puede
  // marcar/desmarcar; el backend también lo revalida.
  const [subActividades, setSubActividades] = useState([]);
  const [actualizandoChecklist, setActualizandoChecklist] = useState(false);

  // Panel para cambiar el estatus -- reemplaza el SweetAlert con un
  // <select> suelto que había antes por un panel con el mismo lenguaje
  // visual del resto del módulo, mostrando solo las transiciones válidas
  // desde el estatus actual.
  const [panelEstatusAbierto, setPanelEstatusAbierto] = useState(false);
  const [nuevoEstatus, setNuevoEstatus] = useState(null);
  const [comentarioEstatus, setComentarioEstatus] = useState("");

  // Visor de evidencia -- reemplaza el SweetAlert genérico (imagen chica,
  // pdf/texto encajados en un popup de tamaño fijo) por un modal propio,
  // grande y con el mismo lenguaje visual del resto de la pantalla.
  const [evidenciaAbierta, setEvidenciaAbierta] = useState(false);
  const [cargandoEvidencia, setCargandoEvidencia] = useState(false);
  const [evidenciaTipo, setEvidenciaTipo] = useState(null); // "imagen" | "pdf" | "texto" | "otro"
  const [evidenciaDataUrl, setEvidenciaDataUrl] = useState("");
  const [evidenciaTexto, setEvidenciaTexto] = useState("");

  const timelineRef = useRef(null);

  const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const f = new Date(fecha);
    return f.toLocaleDateString() + " " + f.toLocaleTimeString();
  };

  const verEvidencia = async () => {
    setEvidenciaAbierta(true);
    setCargandoEvidencia(true);
    setEvidenciaTipo(null);
    setEvidenciaDataUrl("");
    setEvidenciaTexto("");

    try {
      const res = await request.get(`SeguimientoAct/Evidencia/${id}`);
      let evidencia = res?.data?.evidencia || res?.evidencia;

      if (!evidencia) {
        setEvidenciaAbierta(false);
        Swal.fire("Evidencia", "No hay evidencia disponible", "info");
        return;
      }

      const tipoMime = evidencia.substring(0, evidencia.indexOf(";"));
      setEvidenciaDataUrl(evidencia);

      if (tipoMime.includes("image")) {
        setEvidenciaTipo("imagen");
      } else if (tipoMime.includes("pdf")) {
        setEvidenciaTipo("pdf");
      } else if (tipoMime.includes("text") || tipoMime.includes("json")) {
        try {
          setEvidenciaTexto(atob(evidencia.split(",")[1]));
        } catch {
          setEvidenciaTexto("No se pudo leer el contenido de este archivo.");
        }
        setEvidenciaTipo("texto");
      } else {
        setEvidenciaTipo("otro");
      }

    } catch {
      setEvidenciaAbierta(false);
      Swal.fire("Error", "No se pudo obtener la evidencia", "error");
    } finally {
      setCargandoEvidencia(false);
    }
  };

  const cerrarEvidencia = () => {
    setEvidenciaAbierta(false);
    setEvidenciaTipo(null);
    setEvidenciaDataUrl("");
    setEvidenciaTexto("");
  };

  const cargarActividad = async () => {
    try {
      const res = await request.get(`SeguimientoAct/Obtener/${id}`);
      setActividad(res?.data || res);
    } catch {
      Swal.fire("Error", "No se pudo cargar la actividad", "error");
    }
  };

  const cargarHistorial = async () => {
    try {
      const res = await request.get(`SeguimientoAct/Historial/${id}`);
      setHistorial(res?.data || res || []);
    } catch {
      console.error("Error cargando historial");
    }
  };

  const agregarComentario = async () => {
    if (!comentario.trim()) return;

    try {
      setLoading(true);
      await request.post("SeguimientoAct/AgregarComentario", {
        idSolicitud: Number(id),
        comentario: comentario
      });

      setComentario("");
      await cargarHistorial();

    } catch {
      Swal.fire("Error", "No se pudo agregar comentario", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarSubActividades = async () => {
    try {
      const res = await request.get(`SeguimientoAct/SubActividades/${id}`);
      setSubActividades(res?.data || res || []);
    } catch {
      console.error("Error cargando checklist");
    }
  };

  const cargarDatos = async () => {
    setLoader({ mostrar: true });

    try {
      await Promise.all([cargarActividad(), cargarHistorial(), cargarSubActividades()]);
    } finally {
      setLoader({ mostrar: false });
    }
  };

  const esResponsable = Number(actividad?.esResponsable ?? 0) === 1;
  const transicionesDisponibles = obtenerTransicionesDisponibles(actividad.estatus, actividad.tipoSala);
  const puedeEditarEstatus = esResponsable && transicionesDisponibles.length > 0;

  const tieneChecklist = subActividades.length > 0;
  const pendientesChecklist = subActividades.filter((s) => !s.completado).length;

  // Una vez que el ticket llega a un estatus final (Finalizado/Aceptado/
  // Rechazado) el checklist queda congelado tal como quedó -- ya no tiene
  // caso seguir marcándolo, y evita que se "corrija" un ticket ya cerrado.
  const checklistBloqueado = ESTATUS_TERMINALES.includes(actividad.estatus);
  const puedeEditarChecklist = esResponsable && !checklistBloqueado;

  const toggleSubActividad = async (item) => {
    if (!puedeEditarChecklist || actualizandoChecklist) return;

    try {
      setActualizandoChecklist(true);
      await request.post("SeguimientoAct/MarcarSubActividad", {
        idSegActSubActividad: item.idSegActSubActividad,
        idSolicitud: Number(id),
        completado: !item.completado,
      });
      await cargarSubActividades();
    } catch (err) {
      Swal.fire("Error", err?.mensaje || err?.message || "No se pudo actualizar el checklist", "error");
    } finally {
      setActualizandoChecklist(false);
    }
  };

  const abrirPanelEstatus = () => {
    setNuevoEstatus(null);
    setComentarioEstatus("");
    setPanelEstatusAbierto(true);
  };

  const confirmarCambioEstatus = async () => {
    if (!nuevoEstatus) {
      Swal.fire("Aviso", "Selecciona el nuevo estatus", "warning");
      return;
    }

    if (nuevoEstatus === "F" && tieneChecklist && pendientesChecklist > 0) {
      Swal.fire(
        "Checklist incompleto",
        `Quedan ${pendientesChecklist} paso(s) del checklist sin completar. Márcalos como completados antes de finalizar el ticket.`,
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      await request.post("SeguimientoAct/CambiarEstatus", {
        idSolicitud: actividad.idSolicitud,
        estatus: nuevoEstatus,
        comentario: comentarioEstatus || ""
      });

      Swal.fire("Correcto", "Estatus actualizado", "success");
      setPanelEstatusAbierto(false);
      await cargarDatos();

    } catch (err) {
      // request.post (Helpers/API.js) normalmente rechaza con el body ya
      // parseado ({ mensaje: "..." }), pero si la llamada falla antes de
      // eso (endpoint inexistente, error de red, respuesta que no es JSON)
      // lo que llega es un Error de JS normal (con .message, no .mensaje).
      // Se dejan ambos casos cubiertos y se loguea el error crudo para
      // poder diagnosticarlo desde la consola del navegador.
      // eslint-disable-next-line no-console
      console.error("CambiarEstatus error:", err);
      Swal.fire("Error", err?.mensaje || err?.message || "No se pudo actualizar el estatus", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, [historial]);

  // Una vez que se conoce la sala del ticket, revisa si tiene campos extra
  // configurados para poder mostrarlos junto con los datos base.
  useEffect(() => {
    if (!actividad.idSala) {
      setCamposExtraDefinicion([]);
      return;
    }

    request.get(`Sala/CamposExtra/${actividad.idSala}`)
      .then((res) => {
        const data = res?.data ?? res ?? {};
        const crudo = data.camposextra ?? data.camposExtra;
        const definicion = crudo ? JSON.parse(crudo) : [];
        setCamposExtraDefinicion(Array.isArray(definicion) ? definicion : []);
      })
      .catch(() => setCamposExtraDefinicion([]));
  }, [actividad.idSala]);

  const datosExtraValores = (() => {
    if (!actividad.datosExtra) return {};
    try {
      return JSON.parse(actividad.datosExtra);
    } catch {
      return {};
    }
  })();

  return (
    <PlantillaScreen
      titulo={"DETALLE DEL TICKET"}
      className_main={1}
      className={"seguimiento-actividades-contenedor"}
    >

      <ButtonBack path={"/Permisos/CatSeguimientoActividades"} />
      <LoaderScreen loader={loader} />

      <div className="sdt-panel">
        <div className="sdt-layout">

          <div className="sdt-info">

            <div className="sdt-info-header">
              <div className={`sdt-avatar-ring sdt-avatar-ring--${obtenerClaseEstado(actividad.estatus)}`}>
                <Avatar
                  src={actividad.fotoUsuario ? `data:image/jpeg;base64,${actividad.fotoUsuario}` : ""}
                  className="sdt-avatar"
                />
              </div>
              <div className="sdt-info-header-texto">
                <div className="sdt-usuario">{actividad.usuarioNombre || "Usuario"}</div>
                <div className="sdt-folio">{actividad.folio || actividad.idSolicitud}</div>
              </div>
              <span className={`status-badge status-badge--estatus-${obtenerClaseEstado(actividad.estatus)} sdt-estatus-badge`}>
                {obtenerTextoEstado(actividad.estatus)}
              </span>
            </div>

            <div className="sdt-titulo-ticket">
              <div className="sdt-titulo-ticket-texto">
                <span className="sdt-campo-label"><AssignmentIcon /> Actividad</span>
                <h2 className="sdt-titulo-ticket-nombre">{actividad.nombreActividad}</h2>
              </div>
              {obtenerClasePrioridad(actividad.prioridad) && (
                <span className={`status-badge status-badge--prioridad-${obtenerClasePrioridad(actividad.prioridad)}`}>
                  {obtenerTextoPrioridad(actividad.prioridad)}
                </span>
              )}
            </div>

            <div className="sdt-campos">
              <div className="sdt-campo">
                <span className="sdt-campo-label"><ApartmentIcon /> Sala</span>
                <span className="sdt-campo-valor">{actividad.nombreSala}</span>
              </div>

              <div className="sdt-campo">
                <span className="sdt-campo-label"><EventIcon /> Fecha inicio</span>
                <span className="sdt-campo-valor">{formatearFecha(actividad.fechaInicio)}</span>
              </div>

              {actividad.estatus === "F" && (
                <div className="sdt-campo">
                  <span className="sdt-campo-label"><EventIcon /> Fecha término</span>
                  <span className="sdt-campo-valor">{formatearFecha(actividad.fechaTermino)}</span>
                </div>
              )}
            </div>

            {camposExtraDefinicion.length > 0 && (
              <div className="sdt-campos-extra">
                <div className="sdt-campos-extra-titulo">Información adicional de la sala</div>
                <div className="sdt-campos-extra-grid">
                  {camposExtraDefinicion.map((c) => (
                    <div className="sdt-campo" key={c.campo}>
                      <span className="sdt-campo-label">{c.etiqueta}</span>
                      <span className="sdt-campo-valor">{datosExtraValores[c.campo] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="sdt-comentarios">
              <span className="sdt-campo-label"><ChatBubbleOutlineIcon /> Comentarios</span>
              <div className="sdt-comentarios-texto">{actividad.comentarios || "Sin comentarios"}</div>
            </div>

            {tieneChecklist && (
              <div className={`sdt-checklist ${checklistBloqueado ? "is-bloqueado" : ""}`}>
                <div className="sdt-checklist-header">
                  <span className="sdt-checklist-titulo">
                    <PlaylistAddCheckIcon /> Checklist
                    {checklistBloqueado && (
                      <LockIcon titleAccess="El ticket ya está en un estatus final, el checklist no se puede modificar" />
                    )}
                  </span>
                  <span className="sdt-checklist-contador">
                    {subActividades.length - pendientesChecklist}/{subActividades.length}
                  </span>
                </div>

                <div className="sdt-checklist-progreso">
                  <div
                    className="sdt-checklist-progreso-fill"
                    style={{
                      width: `${Math.round(((subActividades.length - pendientesChecklist) / subActividades.length) * 100)}%`,
                    }}
                  />
                </div>

                <div className="sdt-checklist-lista">
                  {subActividades.map((s) => (
                    <label
                      key={s.idSegActSubActividad}
                      className={`sdt-checklist-item ${s.completado ? "is-completado" : ""}`}
                      style={{ cursor: puedeEditarChecklist ? "pointer" : "default" }}
                    >
                      <input
                        type="checkbox"
                        checked={!!s.completado}
                        disabled={!puedeEditarChecklist || actualizandoChecklist}
                        onChange={() => toggleSubActividad(s)}
                      />
                      <span className="sdt-checklist-check">
                        <CheckIcon />
                      </span>
                      <span className="sdt-checklist-texto">
                        <span className="sdt-checklist-nombre">{s.nombreSubActividad}</span>
                        {s.completado && s.usuarioCompletoNombre && (
                          <span className="sdt-checklist-meta">
                            {s.usuarioCompletoNombre} · {formatearFecha(s.fechaCompletado)}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="sdt-acciones">
              <button
                type="button"
                className="sdt-btn sdt-btn--secundario"
                onClick={verEvidencia}
                disabled={!actividad.evidencia}
              >
                <VisibilityIcon /> Ver evidencia
              </button>

              {puedeEditarEstatus && (
                <button
                  type="button"
                  className="sdt-btn sdt-btn--primario"
                  onClick={abrirPanelEstatus}
                >
                  <ChangeCircleIcon /> Editar estatus
                </button>
              )}
            </div>
          </div>

          <div className="sdt-timeline-panel">

            <div className="sdt-timeline-titulo">Historial</div>

            <div className="sdt-timeline" ref={timelineRef}>
              {historial.length === 0 ? (
                <div className="sdt-timeline-vacio">Sin actividad registrada todavía</div>
              ) : (
                historial.map((item, i) => {
                  const clase = item.tipoEvento === "M" ? "comentario" : obtenerClaseEstado(item.tipoEvento);
                  return (
                    <div key={i} className={`sdt-timeline-item sdt-timeline-item--${clase}`}>
                      <div className="sdt-timeline-dot"></div>
                      <div className="sdt-timeline-content">
                        <div className="sdt-timeline-header">
                          <span className="sdt-timeline-evento">{obtenerTextoEstado(item.tipoEvento)}</span>
                          <span className="sdt-timeline-fecha">{formatearFecha(item.createDate)}</span>
                        </div>
                        <div className="sdt-timeline-usuario">{item.usuarioNombre}</div>
                        {item.comentario && <div className="sdt-timeline-comentario">{item.comentario}</div>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="sdt-comentar">
              <textarea
                rows={2}
                placeholder="Agregar comentario..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
              />
              <button
                type="button"
                className="sdt-btn sdt-btn--primario"
                onClick={agregarComentario}
                disabled={loading || !comentario.trim()}
              >
                <SendIcon /> Comentar
              </button>
            </div>
          </div>

        </div>
      </div>

      {panelEstatusAbierto && (
        <div className="sdt-modal-overlay">
          <div className="sdt-modal-card">

            <div className="sdt-modal-header">
              <span className="sdt-modal-header-icon"><ChangeCircleIcon /></span>
              <div className="sdt-modal-header-texto">
                <div className="sdt-modal-titulo">Editar estatus</div>
                <div className="sdt-modal-subtitulo">{actividad.nombreActividad}</div>
              </div>
              <CloseIcon className="sdt-modal-cerrar" onClick={() => setPanelEstatusAbierto(false)} />
            </div>

            <div className="sdt-modal-actual">
              Estatus actual
              <span className={`status-badge status-badge--estatus-${obtenerClaseEstado(actividad.estatus)}`}>
                {obtenerTextoEstado(actividad.estatus)}
              </span>
            </div>

            <div className="sdt-modal-opciones">
              {transicionesDisponibles.map((op) => {
                const bloqueadaPorChecklist = op === "F" && tieneChecklist && pendientesChecklist > 0;
                return (
                  <button
                    key={op}
                    type="button"
                    className={`sdt-modal-opcion sdt-modal-opcion--${obtenerClaseEstado(op)} ${nuevoEstatus === op ? "is-active" : ""}`}
                    onClick={() => !bloqueadaPorChecklist && setNuevoEstatus(op)}
                    disabled={bloqueadaPorChecklist}
                    title={bloqueadaPorChecklist ? `Quedan ${pendientesChecklist} paso(s) del checklist sin completar` : undefined}
                    style={bloqueadaPorChecklist ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >
                    {obtenerTextoEstado(op)}
                  </button>
                );
              })}
            </div>

            {tieneChecklist && pendientesChecklist > 0 && (
              <div className="sdt-modal-alerta">
                <ReportProblemIcon />
                <span>
                  Quedan {pendientesChecklist} paso(s) del checklist sin completar -- no se puede
                  Finalizar hasta terminarlos.
                </span>
              </div>
            )}

            <div className="lead-form-group" style={{ marginTop: 14 }}>
              <label>Comentario (opcional)</label>
              <textarea
                rows={3}
                placeholder="Explica brevemente el motivo del cambio..."
                value={comentarioEstatus}
                onChange={(e) => setComentarioEstatus(e.target.value)}
              />
            </div>

            <div className="sdt-modal-acciones">
              <button
                type="button"
                className="sdt-modal-btn sdt-modal-btn--secundario"
                onClick={() => setPanelEstatusAbierto(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="sdt-modal-btn sdt-modal-btn--primario"
                onClick={confirmarCambioEstatus}
                disabled={!nuevoEstatus || loading}
              >
                Confirmar
              </button>
            </div>

          </div>
        </div>
      )}

      {evidenciaAbierta && (
        <div className="sdt-evidencia-overlay" onClick={cerrarEvidencia}>
          <div className="sdt-evidencia-card" onClick={(e) => e.stopPropagation()}>

            <div className="sdt-modal-header">
              <span className="sdt-modal-header-icon"><InsertDriveFileIcon /></span>
              <div className="sdt-modal-header-texto">
                <div className="sdt-modal-titulo">Evidencia</div>
                <div className="sdt-modal-subtitulo">{actividad.nombreActividad}</div>
              </div>

              {evidenciaDataUrl && (
                <a
                  className="sdt-evidencia-descargar"
                  href={evidenciaDataUrl}
                  download="evidencia"
                  title="Descargar"
                >
                  <DownloadIcon />
                </a>
              )}

              <CloseIcon className="sdt-modal-cerrar" onClick={cerrarEvidencia} />
            </div>

            <div className="sdt-evidencia-body">

              {cargandoEvidencia && (
                <div className="sdt-evidencia-cargando">Cargando evidencia...</div>
              )}

              {!cargandoEvidencia && evidenciaTipo === "imagen" && (
                <img src={evidenciaDataUrl} alt="Evidencia" className="sdt-evidencia-imagen" />
              )}

              {!cargandoEvidencia && evidenciaTipo === "pdf" && (
                <>
                  <iframe src={evidenciaDataUrl} title="Evidencia PDF" className="sdt-evidencia-pdf" />
                  <a
                    className="sdt-evidencia-link-secundario"
                    href={evidenciaDataUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNewIcon /> Abrir en una pestaña nueva
                  </a>
                </>
              )}

              {!cargandoEvidencia && evidenciaTipo === "texto" && (
                <pre className="sdt-evidencia-texto">{evidenciaTexto}</pre>
              )}

              {!cargandoEvidencia && evidenciaTipo === "otro" && (
                <div className="sdt-evidencia-generico">
                  <InsertDriveFileIcon />
                  <div>No se puede previsualizar este tipo de archivo.</div>
                  <a className="sdt-btn sdt-btn--primario" href={evidenciaDataUrl} download="archivo">
                    <DownloadIcon /> Descargar archivo
                  </a>
                </div>
              )}

            </div>

          </div>
        </div>
      )}

      {loading && (
        <div className="nsi-loading-overlay">
          <div className="nsi-loading-card">
            <div className="nsi-loading-spinner">
              <span></span>
              <span></span>
              <ChangeCircleIcon />
            </div>
            <div className="nsi-loading-titulo">Actualizando el ticket</div>
            <div className="nsi-loading-subtitulo">
              Un momento, por favor
              <span className="nsi-loading-dots"><i></i><i></i><i></i></span>
            </div>
          </div>
        </div>
      )}

    </PlantillaScreen>
  );
};
